import React, { useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import TrendChart from "../../Components/Charts/TrendChart";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  BellIcon,
  BriefcaseIcon,
  CalendarIcon,
  ChevronDownIcon,
  ClockIcon,
  ConflictIcon,
  CopyIcon,
  DatabaseIcon,
  DownloadIcon,
  InfoIcon,
  MissingDataIcon,
  MoreIcon,
  ShieldCheckIcon,
  SyncIcon,
  UserIcon,
  UsersIcon,
} from "../../Components/Icons";
import {
  ALERTS,
  ALERT_TABS,
  COMPARISON_DATE,
  COMPLIANCE_TREND,
  DATA_QUALITY,
  KPIS,
  RISK_SEGMENTS,
  SYSTEM_OVERVIEW,
} from "./data";
import "./Dashboard.css";

const ICONS = {
  users: UsersIcon,
  user: UserIcon,
  shield: ShieldCheckIcon,
  database: DatabaseIcon,
  alert: AlertTriangleIcon,
  bell: BellIcon,
  copy: CopyIcon,
  missing: MissingDataIcon,
  conflict: ConflictIcon,
  clock: ClockIcon,
  sync: SyncIcon,
  cases: BriefcaseIcon,
};

const RISK_ICONS = {
  High: AlertTriangleIcon,
  Medium: AlertTriangleIcon,
  Low: AlertCircleIcon,
};

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/\s+/g, "-")}`;

function CardHeader({ title, action }) {
  return (
    <div className="dgi-card__header">
      <h2 className="dgi-card__title">
        {title}
        <span className="dgi-card__info" title={`About ${title}`}>
          <InfoIcon size={15} />
        </span>
      </h2>
      {action ?? (
        <button
          type="button"
          className="dgi-card__more"
          aria-label="More options"
        >
          <MoreIcon size={18} />
        </button>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("all");
  const [granularity, setGranularity] = useState("Monthly");

  const visibleAlerts =
    activeTab === "all"
      ? ALERTS
      : ALERTS.filter((alert) => alert.risk === activeTab);

  return (
    <div className="dashboard">
      {/* --------------------------------------------------------------- */}
      <div className="dashboard__head">
        <div>
          <h1 className="dashboard__title">Executive Dashboard</h1>
          <p className="dashboard__subtitle">
            Overview of the national taxpayer registry and system performance
          </p>
        </div>

        <div className="dashboard__head-actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <CalendarIcon size={17} />
            <span className="dashboard__range">01 Jan 2026 - 01 Sep 2026</span>
            <ChevronDownIcon size={16} />
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <DownloadIcon size={17} />
            Export Report
          </button>
        </div>
      </div>

      {/* --------------------------------------------------------- KPIs -- */}
      <section aria-label="Key metrics">
        <div className="row g-3 kpi-row">
          {KPIS.map((kpi) => {
            const Icon = ICONS[kpi.icon];
            return (
              <div className="col-6 col-md-4" key={kpi.key}>
                <article className="kpi h-100">
                  <span className={`kpi__icon kpi__icon--${kpi.tone}`}>
                    <Icon size={22} />
                  </span>
                  <p className="kpi__label">{kpi.label}</p>
                  <p className="kpi__value">
                    {kpi.value}
                    <span className="kpi__delta">
                      <ArrowUpRightIcon size={13} />
                      {kpi.delta}
                    </span>
                  </p>
                  <p className="kpi__foot">vs {COMPARISON_DATE}</p>
                </article>
              </div>
            );
          })}
        </div>
      </section>

      {/* ------------------------------------------------------- charts -- */}
      <div className="row g-3">
        <div className="col-12 col-xl-3">
          <article className="dgi-card h-100">
            <CardHeader title="Risk Overview" />
            <DonutChart
              segments={RISK_SEGMENTS}
              total="3.20M"
              totalLabel="Total"
            />
            <button type="button" className="dgi-card__link">
              View Risk Dashboard <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>

        <div className="col-12 col-xl-5">
          <article className="dgi-card h-100">
            <CardHeader
              title="Compliance Trend"
              action={
                <label className="select">
                  <select
                    value={granularity}
                    onChange={(event) => setGranularity(event.target.value)}
                    aria-label="Trend granularity"
                  >
                    <option>Monthly</option>
                    <option>Quarterly</option>
                    <option>Yearly</option>
                  </select>
                  <ChevronDownIcon size={16} />
                </label>
              }
            />
            <TrendChart points={COMPLIANCE_TREND} />
            <button type="button" className="dgi-card__link">
              View Full Report <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>

        <div className="col-12 col-xl-4">
          <article className="dgi-card h-100">
            <CardHeader title="Data Quality Overview" />
            <div className="quality-grid">
              {DATA_QUALITY.map((item) => {
                const Icon = ICONS[item.icon];
                return (
                  <div className="quality" key={item.key}>
                    <span className={`quality__icon kpi__icon--${item.tone}`}>
                      <Icon size={20} />
                    </span>
                    <div className="quality__meta">
                      <p className="quality__label">{item.label}</p>
                      <p className="quality__value">
                        {item.value}
                        <span className="kpi__delta">
                          <ArrowUpRightIcon size={12} />
                          {item.delta}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <button type="button" className="dgi-card__link">
              Go to Data Quality <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>
      </div>

      {/* ------------------------------------------------- alerts + sys -- */}
      <div className="row g-3">
        <div className="col-12 col-xl-8">
          <article className="dgi-card h-100">
            <CardHeader
              title="Recent Alerts"
              action={
                <button
                  type="button"
                  className="dgi-card__link dgi-card__link--inline"
                >
                  View All Alerts <ArrowRightIcon size={16} />
                </button>
              }
            />

            <div className="tabs" role="tablist">
              {ALERT_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={`tabs__tab${
                    activeTab === tab.key ? " tabs__tab--active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Table on md and up; the same rows become stacked cards below,
                where seven columns cannot fit without clipping. */}
            <div className="dgi-table-wrap d-none d-md-block">
              <table className="dgi-table">
                <thead>
                  <tr>
                    <th>Alert</th>
                    <th>Taxpayer</th>
                    <th>Type</th>
                    <th>Risk</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visibleAlerts.map((alert) => {
                    const RiskIcon = RISK_ICONS[alert.risk];
                    const tone = alert.risk.toLowerCase();
                    return (
                      <tr key={alert.id}>
                        <td>
                          <div className="alert-cell">
                            <span className={`alert-cell__dot dot--${tone}`} />
                            <RiskIcon size={17} className="alert-cell__icon" />
                            <span className="alert-cell__title">
                              {alert.title}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button type="button" className="link">
                            {alert.taxpayer}
                          </button>
                          <span className="dgi-table__sub">
                            NIF: {alert.nif}
                          </span>
                        </td>
                        <td>{alert.type}</td>
                        <td>
                          <span className={chipClass(alert.risk)}>
                            {alert.risk}
                          </span>
                        </td>
                        <td>
                          {alert.date}
                          <span className="dgi-table__sub">{alert.time}</span>
                        </td>
                        <td>
                          <span className={chipClass(alert.status)}>
                            {alert.status}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="dgi-card__more"
                            aria-label={`Actions for ${alert.title}`}
                          >
                            <MoreIcon size={18} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className="alert-list d-md-none">
              {visibleAlerts.map((alert) => {
                const RiskIcon = RISK_ICONS[alert.risk];
                const tone = alert.risk.toLowerCase();
                return (
                  <li className="alert-item" key={alert.id}>
                    <div className="alert-item__head">
                      <RiskIcon size={17} className={`alert-item__icon--${tone}`} />
                      <p className="alert-item__title">{alert.title}</p>
                      <button
                        type="button"
                        className="dgi-card__more"
                        aria-label={`Actions for ${alert.title}`}
                      >
                        <MoreIcon size={18} />
                      </button>
                    </div>

                    <button type="button" className="link">
                      {alert.taxpayer}
                    </button>
                    <p className="alert-item__meta">
                      NIF: {alert.nif} · {alert.type}
                    </p>

                    <div className="alert-item__foot">
                      <span className={chipClass(alert.risk)}>
                        {alert.risk}
                      </span>
                      <span className={chipClass(alert.status)}>
                        {alert.status}
                      </span>
                      <span className="alert-item__date">
                        {alert.date}, {alert.time}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>

            {visibleAlerts.length === 0 && (
              <p className="dgi-table__empty">No alerts at this risk level.</p>
            )}
          </article>
        </div>

        <div className="col-12 col-xl-4">
          <article className="dgi-card h-100">
            <CardHeader title="System Overview" />
            <ul className="sysview">
              {SYSTEM_OVERVIEW.map((row) => {
                const Icon = ICONS[row.icon];
                return (
                  <li className="sysview__row" key={row.key}>
                    <span className="sysview__icon">
                      <Icon size={20} />
                    </span>
                    <span className="sysview__label">{row.label}</span>
                    <span className="sysview__value">
                      <span
                        className={
                          row.tone === "good"
                            ? "sysview__value--good"
                            : undefined
                        }
                      >
                        {row.value}
                      </span>
                      {row.hint && (
                        <span className="sysview__hint">{row.hint}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className="dgi-card__link dgi-card__link--right"
            >
              View all cases <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>
      </div>
    </div>
  );
}
